import { Box, Typography, Paper, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, InputAdornment } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import HeaderBar from "../components/HeaderBar";
import themeColors from "../theme";
import { clinicalUpdates } from "../data/clinicalUpdates";

function monthYearToSortKey(value) {
    // Supports strings like "Jan 2026", "Dec 2025", etc.
    const match = String(value ?? "").trim().match(/^([A-Za-z]{3})\s+(\d{4})$/);
    if (!match) return -Infinity;

    const [, mon, yearStr] = match;
    const monthIndexByAbbrev = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11
    };
    const monthIndex = monthIndexByAbbrev[mon];
    const year = Number(yearStr);
    if (monthIndex == null || !Number.isFinite(year)) return -Infinity;

    // Higher is newer (e.g., 2026-01 > 2025-12)
    return year * 12 + monthIndex;
}

export default function ClinicalUpdatesPage() {
    const navigate = useNavigate();
    const { teal, lightGray } = themeColors;
    const [searchTerm, setSearchTerm] = useState("");

    const filteredUpdates = clinicalUpdates
        .filter((update) =>
            update.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            update.organization.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .toSorted((a, b) => monthYearToSortKey(b.date) - monthYearToSortKey(a.date));

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: lightGray }}>
            <HeaderBar />

            <Box sx={{ px: { xs: 2, md: 8 }, py: 6 }}>
                <Typography variant="h3" fontWeight="bold" color="#2C3E50" mb={1}>
                    Clinical Updates
                </Typography>
                <Typography variant="body1" color="#6C757D" mb={4}>
                    Practice-changing guideline updates.
                </Typography>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        overflow: 'hidden'
                    }}
                >
                    {/* Header with title and search */}
                    <Box sx={{
                        p: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Typography variant="h6" fontWeight="600">
                            All Updates
                        </Typography>
                        <TextField
                            size="small"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            sx={{ width: 200 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    {/* Table */}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                    <TableCell sx={{ fontWeight: 600, color: teal }}>Guideline</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: teal }}>Organization</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: teal }}>Release Date</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUpdates.map((update) => (
                                    <TableRow
                                        key={update.id}
                                        sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                                    >
                                        <TableCell>{update.title}</TableCell>
                                        <TableCell>{update.organization}</TableCell>
                                        <TableCell>{update.date}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => navigate(`/clinical-updates/${update.slug}`)}
                                                sx={{
                                                    color: teal,
                                                    borderColor: teal,
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        backgroundColor: `${teal}10`,
                                                        borderColor: teal
                                                    }
                                                }}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filteredUpdates.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            No updates found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
}
